import React from "react";
import DesktopHeader from "../../components/DesktopHeader";
import { useAppDispatch, useAppSelector, useDebounce } from "../../app/hooks";
import { selectLabels } from "../../slices/language";
import DesktopBody from "../../components/DesktopBody";
import { DesktopFiltersProps } from "../../components/DesktopFilters/DesktopFilters";
import { CircularProgress } from "@mui/material";
import api from "../../api";
import "./access-points.css";
import TableComponent from "../../components/TableComponent";
import { TableData } from "../../components/TableComponent/TableComponent";
import DesktopDropView from "../../components/DesktopDropView";
import DesktopAccessPointDetails from "../../components/DesktopAccessPointDetails";
import { selectUser, selectUserRole } from "../../slices/user";
import { PropertyArray } from "../../api/models/property-model";
import dayjs from "dayjs";
import { showError } from "../../utils/helpers";
import AccessPointsMap from "../../components/AccessPointsMap";

type MapAccessPoint = {
  id: number;
  name: string;
  latitude: number;
  longitude: number;
};

const hasValidCoordinates = (
  latitude: number | null | undefined,
  longitude: number | null | undefined
): boolean => {
  if (latitude == null || longitude == null) return false;

  const lat = Number(latitude);
  const lng = Number(longitude);

  return !Number.isNaN(lat) && !Number.isNaN(lng);
};

const AccessPoints = () => {
  const labels = useAppSelector(selectLabels);
  const userRole = useAppSelector(selectUserRole);
  const user = useAppSelector(selectUser);
  const dispatch = useAppDispatch();

  const companyId = user?.company?.id;

  const propertyId = user?.property?.id;

  const headers = [
    { label: "ID", key: "ID" },
    { label: labels.name, key: labels.name },
    { label: labels.location, key: labels.location },
    { label: labels.address, key: labels.address },
    { label: labels.gates, key: labels.gates },
    { label: labels.time_zone, key: labels.time_zone },
    { label: labels.latitude, key: labels.latitude },
    { label: labels.longitude, key: labels.longitude },
  ];

  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [searchFilter, setSearchFilter] = React.useState<string>("");
  const [mapAccessPoints, setMapAccessPoints] = React.useState<MapAccessPoint[]>(
    []
  );
  const [page, setPage] = React.useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [totalInvitations, setTotalInvitations] = React.useState<number>(0);
  const [tableDataToDisplay, setTableDataToDisplay] =
    React.useState<TableData | null>(null);
  const [selectedAccessPointId, setSelectedAccessPointId] = React.useState<
    number | null
  >(null);
  const [selectedAccessPointDocumentId, setSelectedAccessPointDocumentId] =
    React.useState<string | null>(null);
  const [accessPointsData, setAccessPointsData] = React.useState<any[]>([]);
  const [showAccessPointDetails, setShowAccessPointDetails] =
    React.useState<boolean>(false);
  const [properties, setProperties] = React.useState<PropertyArray | null>(
    null,
  );
  const [selectedProperty, setSelectedProperty] = React.useState<string | null>(
    null,
  );
  const [propertiesFullData, setPropertiesFullData] =
    React.useState<PropertyArray | null>(null);
  const [dataTable, setDataTable] = React.useState<any[]>([]);
  const [focusedMapAccessPointId, setFocusedMapAccessPointId] = React.useState<
    number | null
  >(null);

  const debouncedSearchText = useDebounce(searchFilter, 500);

  const propertiesArray = properties
    ? properties.data.map((item) => {
        return {
          label: item?.name || "",
          value: `${item.id}`,
        };
      })
    : [];

  const fixedPage = React.useMemo(() => {
    return page + 1;
  }, [page]);

  const selectedAccessPointName =
    tableDataToDisplay?.body.filter(
      (item) => item?.row === selectedAccessPointId,
    )[0]?.cells[0]?.label[0]?.mainLabel || "";

  const currentDate = dayjs().format("DD-MM-YY");

  const selectedPropertyName =
    propertiesFullData?.data.filter(
      (item) => item?.id === Number(selectedProperty),
    )[0] || null;

  const filters: DesktopFiltersProps = {
    filterData:
      userRole === "ciso"
        ? [
            {
              type: "selector",
              value: selectedProperty,
              setValue: setSelectedProperty,
              disabled: isLoading,
              tooltip: "",
              function: () => {},
              array: propertiesArray,
              size: "small",
              icon: "",
              label: labels.access_points,
              multiDateValue: null,
              setMultiDateValue: () => {},
              filterWidth: 350,
            },
            {
              type: "search",
              value: searchFilter,
              setValue: setSearchFilter,
              disabled: isLoading,
              tooltip: "",
              function: () => {},
              array: [],
              size: "small",
              icon: "",
              label: "",
              multiDateValue: null,
              setMultiDateValue: () => {},
              filterWidth: 350,
            },
            {
              type: "function",
              value: "",
              setValue: () => {},
              disabled: isLoading,
              tooltip: labels.download_content,
              function: () => {},
              array: [],
              size: "small",
              icon: "fa-solid:file-download",
              label: "",
              multiDateValue: null,
              setMultiDateValue: () => {},
              downloadable: true,
              downloadData: dataTable,
              downloadHeader: headers,
              downloadFilename: `${labels.access_points}-${selectedPropertyName?.name} ${currentDate}`,
            },
          ]
        : [
            {
              type: "search",
              value: searchFilter,
              setValue: setSearchFilter,
              disabled: isLoading,
              tooltip: "",
              function: () => {},
              array: [],
              size: "small",
              icon: "",
              label: "",
              multiDateValue: null,
              setMultiDateValue: () => {},
              filterWidth: 350,
            },
            {
              type: "function",
              value: "",
              setValue: () => {},
              disabled: isLoading,
              tooltip: labels.download_content,
              function: () => {},
              array: [],
              size: "small",
              icon: "fa-solid:file-download",
              label: "",
              multiDateValue: null,
              setMultiDateValue: () => {},
              downloadable: true,
              downloadData: dataTable,
              downloadHeader: headers,
              downloadFilename: `${labels.access_points}-${selectedPropertyName?.name} ${currentDate}`,
            },
          ],
  };

  React.useEffect(() => {
    getAccessPoints(debouncedSearchText);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    labels,
    fixedPage,
    rowsPerPage,
    userRole,
    companyId,
    selectedProperty,
    debouncedSearchText,
  ]);

  const getAccessPoints = async (debouncedSearchText: string) => {
    if (!companyId || !propertyId) return;
    try {
      setIsLoading(true);
      let response;
      if (userRole === "ciso") {
        if (companyId && selectedProperty) {
          response = await api.accessPointsCISORequest(
            companyId,
            Number(selectedProperty),
            page,
            rowsPerPage,
            debouncedSearchText,
          );
        }
      } else {
        response = await api.accessPointsRequest(
          Number(propertyId),
          page,
          rowsPerPage,
          debouncedSearchText,
        );
      }
      if (response) {
        setAccessPointsData(response.data);
        const dataForDownloadableTable = response.data.map((item) => {
          return {
            ID: `${item?.id}`,
            [labels.name]: `${item?.name}`,
            [labels.location]: `${item?.vanue_name}`,
            [labels.address]: `${item?.address}`,
            [labels.gates]: `${item?.gates?.length}`,
            [labels.time_zone]: `${item?.time_zone}`,
            [labels.latitude]: `${item?.latitude}`,
            [labels.longitude]: `${item?.longitude}`,
          };
        });
        setDataTable(dataForDownloadableTable);
        const totalRows = response?.meta?.pagination?.total;
        setTotalInvitations(totalRows);
        const pointsWithCoordinates: MapAccessPoint[] = response.data
          .filter((item) =>
            hasValidCoordinates(item?.latitude, item?.longitude),
          )
          .map((item) => ({
            id: item.id,
            name: item.name,
            latitude: Number(item.latitude),
            longitude: Number(item.longitude),
          }));
        setMapAccessPoints(pointsWithCoordinates);
        const showMapColumn = pointsWithCoordinates.length > 0;
        const dataForTable: TableData = {
          header: [
            {
              label: labels.name,
              isIcon: false,
              tooltipLabel: "",
            },
            {
              label: labels.location,
              isIcon: false,
              tooltipLabel: "",
            },
            {
              label: labels.gates,
              isIcon: false,
              tooltipLabel: "",
            },
            ...(showMapColumn
              ? [
                  {
                    label: "",
                    isIcon: false,
                    tooltipLabel: "",
                  },
                ]
              : []),
          ],
          body: response.data.map((item) => {
            const itemHasCoordinates = hasValidCoordinates(
              item?.latitude,
              item?.longitude,
            );

            return {
              row: item.id,
              cells: [
                {
                  label: [
                    {
                      mainLabel: item?.name,
                      tooltipLabel: "",
                    },
                  ],
                  isIcon: false,
                  display: "flex-dir-row",
                  isButton: false,
                },
                {
                  label: [
                    {
                      mainLabel: item?.vanue_name,
                      tooltipLabel: "",
                    },
                  ],
                  isIcon: false,
                  display: "flex-dir-row",
                  isButton: false,
                },
                {
                  label: [
                    {
                      mainLabel: `${item?.gates?.length}`,
                      tooltipLabel: "",
                    },
                  ],
                  isIcon: false,
                  display: "flex-dir-row",
                  isButton: false,
                },
                ...(showMapColumn
                  ? [
                      itemHasCoordinates
                        ? {
                            label: [
                              {
                                mainLabel: "material-symbols:my-location",
                                tooltipLabel: labels.center_on_map,
                              },
                            ],
                            isIcon: true,
                            display: "flex-dir-row" as const,
                            isButton: false,
                            buttonFunction: () => {
                              setFocusedMapAccessPointId(item.id);
                            },
                          }
                        : {
                            label: [
                              {
                                mainLabel: "",
                                tooltipLabel: "",
                              },
                            ],
                            isIcon: false,
                            display: "flex-dir-row" as const,
                            isButton: false,
                          },
                    ]
                  : []),
              ],
            };
          }),
        };
        setTableDataToDisplay(dataForTable);
      }
    } catch (error: any) {
      console.error(error);
      const errorMessage = error?.response?.data?.error?.message;
      showError(dispatch, errorMessage ? errorMessage : labels.generic_error);
    } finally {
      setIsLoading(false);
    }
  };

  React.useEffect(() => {
    getProperties();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const getProperties = async () => {
    if (companyId) {
      try {
        setIsLoading(true);
        const propertyResponse =
          await api.propertiesByCompanyRequest(companyId);
        if (propertyResponse) {
          setPropertiesFullData(propertyResponse);
          setProperties(propertyResponse);
          setSelectedProperty(`${propertyResponse?.data[0]?.id}`);
        }
      } catch (error: any) {
        console.error(error);
        const errorMessage = error?.response?.data?.error?.message;
        showError(dispatch, errorMessage ? errorMessage : labels.generic_error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <div className="w-100">
      <DesktopHeader title={labels.access_points} />
      <DesktopBody hasFilters={true} filterData={filters.filterData}>
        <div className="w-100 h-100 px-8 pb-8 border-box">
          {isLoading ? (
            <div className="w-100 h-100 border-box flex align-items-center justify-content-center">
              <CircularProgress />
            </div>
          ) : (
            <div className="w-100 h-100 border-box flex gap-8">
              <div className="w-50 h-100 flex">
                <div className="w-100 h-100 pb-8 border-box">
                  {isLoading ? (
                    <div className="w-100 h-100 border-box flex align-items-center justify-content-center">
                      <CircularProgress />
                    </div>
                  ) : (
                    <TableComponent
                      selectableRow={true}
                      data={tableDataToDisplay}
                      onPressRow={(id: number) => {
                        setSelectedAccessPointId(id);
                        setSelectedAccessPointDocumentId(
                          accessPointsData.find((item) => item?.id === id)
                            ?.documentId ?? null,
                        );
                        setShowAccessPointDetails(true);
                      }}
                      page={page}
                      setPage={setPage}
                      rowsPerPage={rowsPerPage}
                      setRowsPerPage={setRowsPerPage}
                      total={totalInvitations}
                      tableHeight={"calc(100vh - 188px)"}
                      emptyMessage={labels.no_access_points_registered}
                    />
                  )}
                </div>
              </div>
              <div className="w-50 h-100 flex map-container">
                {mapAccessPoints.length > 0 ? (
                  <AccessPointsMap
                    accessPoints={mapAccessPoints}
                    focusedAccessPointId={focusedMapAccessPointId}
                    onResetFocus={() => setFocusedMapAccessPointId(null)}
                    height="calc(100vh - 188px)"
                  />
                ) : (
                  <div className="w-100 h-100 flex align-items-center justify-content-center px-16 border-box">
                    <p className="body-text-md text-center">
                      {labels.no_map_data_for_access_points}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </DesktopBody>
      <DesktopDropView
        showView={showAccessPointDetails}
        setShowView={setShowAccessPointDetails}
        content={
          <div className="flex h-100 border-box">
            <div className="w-100 h-100 border-box p-8 scroll">
              <DesktopAccessPointDetails
                accessPointId={selectedAccessPointDocumentId}
                setShowView={setShowAccessPointDetails}
                extraFunc={() => getAccessPoints(debouncedSearchText)}
              />
            </div>
          </div>
        }
        extraFunc={false}
        title={selectedAccessPointName}
      />
    </div>
  );
};

export default AccessPoints;
